import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface UseSearchOptions<T> {
  searchBy: (item: T) => string[];
  debounceTime?: number;
  resetPageNumber?: () => void;
}

export function useSearch<T>(
  data: T[] = [],
  { searchBy, debounceTime = 500, resetPageNumber }: UseSearchOptions<T>
) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const resetPage = () => {
    if (resetPageNumber) {
      resetPageNumber();
    }
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setDebouncedTerm(searchTerm);
      resetPage();
    }, debounceTime);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, debounceTime]);

  const filteredData = useMemo(() => {
    if (!debouncedTerm.trim()) return data;

    const searchTermInLowerCase = debouncedTerm.toLowerCase();

    return data.filter((item) =>
      searchBy(item).some((field) =>
        field.toLowerCase().includes(searchTermInLowerCase)
      )
    );
  }, [data, debouncedTerm, searchBy]);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  return {
    searchTerm,
    filteredData,
    setSearchTerm: handleSearchChange,
  };
}
